import React, { useCallback, useEffect, useState } from 'react';
import { ROLES } from '../domain/constants';
import { creditsPerRemainingSlot, roleCount, sortRoster } from '../domain/team';

// Una colonna squadra. Il roster arriva come {player_id, role, purchase_price}
// e i dati del giocatore si leggono dal catalogo: il server non trasmette piu
// il record completo da 19 campi per ogni giocatore comprato.

function BudgetMeter({ team, playersPerTeam }) {
    const perSlot = creditsPerRemainingSlot(team, playersPerTeam);
    if (!perSlot) return null;
    const level = perSlot < 1.5 ? 'critical' : perSlot < 4 ? 'low' : '';
    return (
        <span
            className={`credits${level ? ` credits--${level}` : ''}`}
            title="Crediti disponibili per ogni slot ancora da riempire"
        >
            {perSlot.toFixed(1)} /slot
        </span>
    );
}

// Il nome di un giocatore in rosa, con l'anteprima della figurina.
//
// L'immagine si monta al primo passaggio del mouse, non insieme alla riga.
// Ogni figurina di content.fantacalcio.it pesa circa 130 KB: con otto rose
// piene sono duecento immagini, venticinque megabyte scaricati per mostrarne
// forse una. Il `display: none` del foglio di stile non bastava — il browser
// scarica comunque le immagini nascoste, e su una connessione di casa quel
// traffico affamava tutto il resto della pagina.
//
// Una volta chiesta resta montata: il secondo passaggio dev'essere immediato.
function RosterName({ entry, player }) {
    const url = player?.['URL Immagine'];
    const [wanted, setWanted] = useState(false);
    const want = useCallback(() => setWanted(true), []);

    return (
        <span
            className="roster-name"
            tabIndex={url ? 0 : undefined}
            onPointerEnter={url ? want : undefined}
            onFocus={url ? want : undefined}
        >
            {/* Il taglio del nome sta qui dentro e non sul contenitore:
                l'anteprima e figlia sua, e l'overflow:hidden del troncamento
                la ritagliava via del tutto. */}
            <span className="roster-player u-truncate">
                {player?.['Nome Completo'] ?? `#${entry.player_id}`} ({entry.role})
            </span>
            {url && wanted && (
                <img
                    src={url}
                    alt={player['Nome Completo']}
                    className="player-hover-image"
                />
            )}
        </span>
    );
}

export default function TeamColumn({
    name,
    teamName,
    team,
    catalogue = {},
    playersPerTeam,
    isOnline,
    isCurrentTurn,
    hasProxyBid,
    onRename,
    onRemoveParticipant,
    onRemovePlayer,
    onReleasePlayer,
}) {
    const [draftName, setDraftName] = useState(name);

    useEffect(() => {
        setDraftName(name);
    }, [name]);

    const roster = sortRoster(team?.roster);

    return (
        <div className={`team panel${isCurrentTurn ? ' is-current-turn' : ''}`}>
            <div className="team-header panel-header">
                <div className="team-header-row">
                    {isOnline !== undefined && (
                        <span
                            className={`team-dot${isOnline ? '' : ' team-dot--offline'}`}
                            title={isOnline ? 'Online' : 'Offline'}
                            aria-label={isOnline ? 'Online' : 'Offline'}
                        />
                    )}
                    <input
                        type="text"
                        value={draftName}
                        onChange={(event) => setDraftName(event.target.value)}
                        onBlur={() => {
                            if (draftName.trim() && draftName !== name) onRename(name, draftName.trim());
                            else setDraftName(name);
                        }}
                        className="input input--bare team-name"
                        readOnly={!onRename}
                        aria-label={`Nome di ${name}`}
                    />
                    {onRemoveParticipant && (
                        <button
                            type="button"
                            onClick={() => onRemoveParticipant(name)}
                            className="btn btn--icon btn--quiet"
                            aria-label={`Rimuovi ${name}`}
                        >
                            &times;
                        </button>
                    )}
                </div>
                {teamName && teamName !== name && (
                    <div className="team-alias u-dim">Nome Squadra: {teamName}</div>
                )}
                <div className="team-budget">
                    💰Budget: {team?.budget ?? 0}
                    <BudgetMeter team={team} playersPerTeam={playersPerTeam} />
                    {hasProxyBid && <span title="Offerta automatica attiva"> 🤖</span>}
                </div>
                {playersPerTeam && (
                    <div className="team-roles">
                        {ROLES.map((role) => (
                            <span key={role} className="team-role" data-role={role}>
                                {role} {roleCount(team, role)}/{playersPerTeam[role]}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <div className="team-roster">
                {roster.length === 0 && <div className="list-empty">Nessun giocatore.</div>}
                {roster.map((entry) => {
                    const player = catalogue[entry.player_id];
                    return (
                        <div key={entry.player_id} className="roster-row" data-role={entry.role}>
                            <RosterName entry={entry} player={player} />
                            <span className="roster-price num">{entry.purchase_price}</span>
                            <span className="roster-actions">
                                {onReleasePlayer && (
                                    <button
                                        type="button"
                                        onClick={() => onReleasePlayer(name, entry)}
                                        className="btn btn--go btn--sm btn--icon"
                                        title="Svincola giocatore"
                                        aria-label="Svincola giocatore"
                                    >
                                        <span className="roster-action-icon" aria-hidden="true">💵</span>
                                        <span className="roster-action-label">Svincola💵</span>
                                    </button>
                                )}
                                {onRemovePlayer && (
                                    <button
                                        type="button"
                                        onClick={() => onRemovePlayer(name, entry)}
                                        className="btn btn--stop btn--sm btn--icon"
                                        aria-label="Rimuovi giocatore"
                                    >
                                        &times;
                                    </button>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
